import React, { useEffect, useState } from "react";
import { ToggleLeft, ToggleRight, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { listAdminFlags, setAdminFlag } from "../lib/api";

/**
 * V2.0.72 — Admin feature-flag card. Rendered inside the Admin Console.
 * Currently gates the "prompt clarification layer" — when on, the AI
 * asks a one-line follow-up before restructuring geometry if the user's
 * prompt is ambiguous about placement (e.g. "move X into Y" — inside
 * walls vs attached).
 */
const FLAG_DEFS = [
  {
    key: "prompt_clarification_enabled",
    label: "Prompt clarification layer",
    hint:
      "When ON, the AI asks a one-line follow-up before restructuring geometry if the user's prompt is ambiguous about placement (inside walls vs attached / adjacent). Off by default — flip on for beta cohorts to reduce 'that's not what I asked for' feedback.",
  },
];

export default function FeatureFlagsCard() {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    let alive = true;
    listAdminFlags()
      .then((r) => {
        if (alive) setFlags(r.data?.flags || {});
      })
      .catch((err) => {
        console.debug("[FeatureFlagsCard] load failed", err);
        if (alive) setFlags({});
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleToggle = async (key) => {
    if (!flags) return;
    const next = !flags[key];
    setSaving(key);
    // Optimistic
    setFlags((f) => ({ ...f, [key]: next }));
    try {
      await setAdminFlag(key, next);
      const def = FLAG_DEFS.find((d) => d.key === key);
      toast.success(`${def?.label || key} → ${next ? "ON" : "OFF"}`);
    } catch (err) {
      // Roll back
      setFlags((f) => ({ ...f, [key]: !next }));
      toast.error(
        err?.response?.data?.detail || err?.message || "Failed to update flag",
      );
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      className="border border-zinc-800 bg-zinc-950/60 mb-6"
      data-testid="admin-feature-flags-card"
    >
      <div className="px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/60 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
        <span>// FEATURE FLAGS</span>
        <span className="text-brass-400">{FLAG_DEFS.length} toggle{FLAG_DEFS.length === 1 ? "" : "s"}</span>
      </div>
      <div className="divide-y divide-zinc-800">
        {FLAG_DEFS.map((def) => {
          const value = !!flags?.[def.key];
          const busy = saving === def.key;
          const loaded = flags !== null;
          return (
            <div
              key={def.key}
              data-testid={`admin-flag-row-${def.key}`}
              className="flex items-start gap-4 px-4 py-3.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-100 text-sm">{def.label}</span>
                  <span
                    className="text-zinc-600 hover:text-brass-400 cursor-help"
                    title={def.hint}
                  >
                    <HelpCircle className="w-3 h-3" />
                  </span>
                </div>
                <p className="text-zinc-500 text-xs mt-1 leading-relaxed pr-4">
                  {def.hint}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle(def.key)}
                disabled={busy || !loaded}
                data-testid={`admin-flag-toggle-${def.key}`}
                className={
                  "inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors " +
                  (value
                    ? "bg-brass-600 text-zinc-950 border-brass-600 hover:bg-brass-500"
                    : "text-zinc-400 border-zinc-700 hover:border-brass-500 hover:text-brass-200") +
                  (busy || !loaded ? " opacity-60 cursor-wait" : "")
                }
              >
                {value ? (
                  <ToggleRight className="w-3.5 h-3.5" />
                ) : (
                  <ToggleLeft className="w-3.5 h-3.5" />
                )}
                {!loaded ? "loading" : value ? "ON" : "OFF"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
